import { useState, useEffect, useCallback, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Spinner } from "react-bootstrap";
import { toast } from "sonner";

// ==================== CONFIGURATION ====================
const CATEGORY_CONFIG = {
  'Particulier': { color: '#3B82F6', icon: '👤' },
  'OPC': { color: '#4b6cb7', icon: '🏛️' },
  'Entreprises': { color: '#10B981', icon: '🏢' }
};

// ==================== UTILITAIRES ====================
const groupByCategory = (data, valueKey) => {
  const categories = { Particulier: 0, OPC: 0, Entreprises: 0 };

  data?.forEach((item) => {
    const type = item.accountType || item.accountTypeName || item.clientType;
    const value = item[valueKey] ?? 0;

    // 1) Particulier
    if (type === "Particulier") {
      categories.Particulier += value;
      return;
    }

    // 2) OPC
    if (["Opc", "Opcvm", "Fia", "OPC", "OPCVM"].includes(type)) {
      categories.OPC += value;
      return;
    }

    // 3) Issuer => on ignore
    if (type === "Issuer") {
      return;
    }

    // 4) Entreprises
    categories.Entreprises += value;
  });

  return Object.entries(categories).map(([name, value]) => ({
    name,
    value,
    ...CATEGORY_CONFIG[name],
  }));
};


const lightenColor = (hex, percent) => {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.min(255, (num >> 16) + Math.round((255 - (num >> 16)) * percent));
  const g = Math.min(255, ((num >> 8) & 0xFF) + Math.round((255 - ((num >> 8) & 0xFF)) * percent));
  const b = Math.min(255, (num & 0xFF) + Math.round((255 - (num & 0xFF)) * percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

const getContrastColor = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#000" : "#fff";
};

// ==================== COMPOSANT TOOLTIP ====================
const CustomTooltip = ({ active, payload, total, unit = '' }) => {
  if (!active || !payload?.[0]) return null;

  const { color, name, value } = payload[0].payload;
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

  return (
    <div
      style={{
        backgroundColor: lightenColor(color, 0.6),
        color: getContrastColor(lightenColor(color, 0.6)),
        padding: "12px 16px",
        borderRadius: "8px",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
        border: `1px solid ${color}30`,
        fontSize: "14px",
        fontWeight: "600",
      }}
    >
      {`${name}: ${value.toLocaleString('fr-FR')}${unit} (${percentage}%)`}
    </div>
  );
};

// ==================== COMPOSANT PRINCIPAL ====================
const DonutChart = ({
  title,
  subtitle,
  fetchData,
  valueKey,
  unit = '',
  formatValue = (v) => v.toLocaleString('fr-FR'),
  centerLabel = '',
  selectedYear = null,
  delay = "1s"
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [hasShownError, setHasShownError] = useState(false);

  // Chargement des données
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchData();
      
      // Vérifier si la réponse est valide
      if (!response || (Array.isArray(response) && response.length === 0)) {
        // On garde toujours les 3 catégories à 0
        setData(groupByCategory([], valueKey));
        return;
      }
      
      const grouped = groupByCategory(response, valueKey);
      setData(grouped);
      setHasShownError(false); // Réinitialiser l'état d'erreur en cas de succès
    } catch (err) {
      // Ne pas afficher le toast si on a déjà montré une erreur pour ce composant
      if (!hasShownError) {
        setError(err);
        // Vérifier si c'est une vraie erreur réseau et pas juste des données vides
        if (err.message && !err.message.includes('undefined') && !err.message.includes('null')) {
          // toast.error(`Erreur lors du chargement: ${title}`);
        }
        setHasShownError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchData, valueKey, title, hasShownError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calculs mémorisés
  const total = useMemo(() => 
    data.reduce((acc, item) => acc + item.value, 0), 
    [data]
  );

  const chartCells = useMemo(() => 
    data.map((entry, i) => ({
      ...entry,
      fill: entry.value === 0 ? `${entry.color}20` : 
            activeIndex === i ? `${entry.color}99` : entry.color,
      stroke: entry.color,
      strokeWidth: activeIndex === i ? 3 : 1
    })), 
    [data, activeIndex]
  );

  // Gestionnaires
  const handleMouseEnter = useCallback((i) => setActiveIndex(i), []);
  const handleMouseLeave = useCallback(() => setActiveIndex(null), []);

  // Label personnalisé
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
    if (value === 0 || total === 0) return null;
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${((value / total) * 100).toFixed(0)}%`}
      </text>
    );
  };

  // ==================== RENDU ====================
  
  // Chargement
  if (loading) {
    return (
      <div className="col-xl-4 col-lg-6 col-md-12 mb-4 animate-fade-in" style={{ animationDelay: delay, marginBottom: '2rem !important' }}>
        <div className="card h-100 border-0 shadow-sm">
          <div className="card-body d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted mb-0">Chargement...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Erreur
  if (error) {
    return (
      <div className="col-xl-4 col-lg-6 col-md-12 mb-4 animate-fade-in" style={{ animationDelay: delay, marginBottom: '2rem !important' }}>
        <div className="card h-100 border-0 shadow-sm">
          <div className="card-body d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="error-icon-wrapper mb-3">
                <i className="fas fa-exclamation-triangle text-danger"></i>
              </div>
              <p className="text-muted mb-3">Erreur de chargement</p>
              <button 
                className="btn btn-outline-primary btn-retry" 
                onClick={() => {
                  setHasShownError(false);
                  loadData();
                }}
              >
                <i className="fas fa-redo-alt me-2"></i>
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-xl-4 col-lg-6 col-md-12 mb-4 animate-fade-in" style={{ animationDelay: delay, marginBottom: '2rem !important' }}>
      <div className="card h-100 border-0 shadow-sm hover-lift">
        {/* En-tête */}
        <div className="card-header bg-white border-0 pb-0">
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <h5 className="card-title mb-1 fw-bold text-dark">{title}</h5>
              <p className="text-muted small mb-0">
                {subtitle}
              </p>
            </div>
            {selectedYear && (
              <span className="year-badge-professional">{selectedYear}</span>
            )}
          </div>
        </div>

        {/* Corps */}
        <div className="card-body pt-2">
          {/* Graphique */}
          <div className="text-center mb-3">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={chartCells}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={85}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={2}
                  label={renderLabel}
                  labelLine={false}
                >
                  {chartCells.map((entry, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={entry.fill}
                      stroke={entry.stroke}
                      strokeWidth={entry.strokeWidth}
                      onMouseEnter={() => handleMouseEnter(i)}
                      onMouseLeave={handleMouseLeave}
                      style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip total={total} unit={unit} />} />
                
                {/* Centre */}
                <text x="50%" y="45%" textAnchor="middle" dominantBaseline="middle" className="fw-bold" fontSize="20" fill="#1F2937">
                  {formatValue(total)}
                </text>
                <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" className="text-muted" fontSize="12" fill="#6B7280">
                  {centerLabel}
                </text>
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Légende */}
          <div>
            <h6 className="text-uppercase small fw-bold text-muted mb-2">
              Catégories {selectedYear && `(${selectedYear})`}
            </h6>
            <div className="d-flex flex-column gap-2">
              {data.map((item, i) => {
                const isActive = activeIndex === i;
                const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                
                return (
                  <div
                    key={i}
                    className="legend-item p-2 rounded"
                    style={{
                      backgroundColor: isActive ? `${item.color}15` : 
                                     item.value === 0 ? `${item.color}08` : '#f8f9fa',
                      borderLeft: `4px solid ${item.color}`,
                      cursor: 'pointer',
                      opacity: item.value === 0 ? 0.7 : 1,
                      transform: isActive ? 'translateX(4px)' : 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={() => handleMouseEnter(i)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <span className="fs-5">{item.icon}</span>
                        <div>
                          <div className="fw-semibold small text-dark">
                            {item.name}
                            {item.value === 0 && <span className="text-muted ms-1">(Aucun)</span>}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {item.value === 0 ? '0%' : `${percentage}%`} du total
                          </div>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className={`fw-bold small ${item.value === 0 ? 'text-muted' : 'text-dark'}`}>
                          {formatValue(item.value)}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>{centerLabel}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeIn 0.6s ease-out;
        }

        .hover-lift {
          transition: all 0.3s ease;
        }

        .hover-lift:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12) !important;
        }
        .hover-lift {
          border-radius: 9px;
          overflow: hidden;
        }


        .legend-item:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        /* Badge année professionnel */
        
        .year-badge-professional {
          position: absolute;
          top: 10px;
          right: 10px;
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 0.2rem 0.6rem;
          border-radius: 12px;
        }

        /* Bouton retry professionnel */
        .btn-retry {
          border-radius: 25px;
          padding: 0.5rem 1.25rem;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          border-width: 2px;
        }

        .btn-retry:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,123,255,0.3);
        }

        /* Icône d'erreur stylisée */
        .error-icon-wrapper {
          width: 60px;
          height: 60px;
          background: rgba(220, 53, 69, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }

        .error-icon-wrapper i {
          font-size: 1.5rem;
        }

        @media (max-width: 1199px) {
          .col-xl-4 {
            max-width: 50%;
          }
        }

        @media (max-width: 991px) {
          .col-lg-6 {
            max-width: 100%;
          }
        }

        @media (max-width: 767px) {
          .card-title {
            font-size: 1rem;
          }
          
          .legend-item {
            padding: 0.75rem !important;
          }

          .year-badge-professional {
            font-size: 0.7rem;
            padding: 0.25rem 0.6rem;
          }
        }
      `}</style>
    </div>
  );
};

export default DonutChart;